// The folder/device tree model + helpers used by the vault workspace. A vault's folders come from
// the API as a flat list; the navigator renders them as a depth-first tree, and device metadata /
// folder names are client-decrypted under the org key.

import type { DeviceMeta } from "../vault/devices";
import { b64decode, decryptMeta, decryptName } from "../crypto/vault";

export interface FlatFolder {
  id: string;
  parentId: string | null;
  name: string;
  kind: "folder" | "device";
  meta: DeviceMeta | null;
}

export interface FolderNode extends FlatFolder {
  depth: number;
}

// Order nodes as a depth-first tree (RDM-style nesting) and stamp each node's depth.
export function buildFolderTree(flat: FlatFolder[]): FolderNode[] {
  const byParent = new Map<string | null, FlatFolder[]>();
  for (const f of flat) {
    const arr = byParent.get(f.parentId) ?? [];
    arr.push(f);
    byParent.set(f.parentId, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
  const out: FolderNode[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const f of byParent.get(parentId) ?? []) {
      out.push({ ...f, depth });
      walk(f.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** "Servers / DMZ / db01" for a folder id — the destination breadcrumb in the item editor. */
export function folderPathLabel(folders: FolderNode[], id: string | null): string {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const parts: string[] = [];
  let cur = id;
  while (cur) {
    const node = byId.get(cur);
    if (!node) break;
    parts.unshift(node.name);
    cur = node.parentId;
  }
  return parts.join(" / ");
}

export function safeMeta(orgKey: Uint8Array, encMetaB64: string): DeviceMeta {
  try {
    return decryptMeta<DeviceMeta>(orgKey, b64decode(encMetaB64));
  } catch {
    return {};
  }
}

export function safeName(orgKey: Uint8Array, encNameB64: string): string {
  try {
    return decryptName(orgKey, b64decode(encNameB64));
  } catch {
    return "(folder)";
  }
}
